import { Alert, Box, Flex, InlineValidation, PrimaryButton, Select, Text } from "@nulogy/components";
import classNames from "classnames";
import ApolloLoadingAnimation from "components/common/ApolloLoadingAnimation";
import ModalFooter from "components/common/ModalFooter";
import DatePicker from "components/controls/DatePicker";
import Form from "components/controls/Form";
import { MAX_JOB_COUNT } from "components/pages/schedule/publish/publishConstants";
import sortBy from "lodash/sortBy";
import uniqBy from "lodash/uniqBy";
import React, { useState } from "react";
import styled from "styled-components";
import FilterLabel from "../FilterLabel";
import ExpandFiltersButton from "./ExpandFiltersButton";
import JobCount from "./JobCount";

const FORM_ID = "publish-modal";

const DROPDOWN_ROW_HEIGHT = 38; // px

const OptionalNotice = styled(Text)(({ theme }) => ({
  color: theme.colors.darkGrey,
  display: "inline",
  fontSize: theme.fontSizes.smaller,
  fontWeight: theme.fontWeights.normal,
}));

const DateRangeSeparator = styled.div`
  padding: 7px;
  text-align: center;
`;
function formErrors(response) {
  const configurationErrors = response.errors.filter((error) => error.path.length !== 3);

  return configurationErrors.length ? configurationErrors : null;
}

export default function PublishScheduleFormView({ onSave, response, ...contentProps }) {
  return (
    <Form errors={formErrors(response)} id={FORM_ID} onSave={onSave}>
      {({ isSubmitting, values }) => <Content isSubmitting={isSubmitting} values={values} {...contentProps} />}
    </Form>
  );
}

function nothingToPublishMessage({ jobCounts: { total }, searchingByLine }) {
  if (total) return null;

  return searchingByLine
    ? "There are no scheduled blocks within the specified filters."
    : "There are no scheduled blocks within the specified date range.";
}

function jobsCreatedAndUpdatedMessage({ jobCounts: { toCreate, toUpdate } }) {
  if (!toCreate && !toUpdate) return null;

  return (
    <>
      <Box fontSize="smaller" lineHeight="smallerText" marginBottom="half">
        Jobs to be created: <JobCount>{toCreate}</JobCount>
      </Box>
      <Box fontSize="smaller" lineHeight="smallerText">
        Jobs to be updated: <JobCount>{toUpdate}</JobCount>
      </Box>
    </>
  );
}

export function Content({
  isSubmitting,
  onFilterChange,
  showJobCount,
  filters,
  lines,
  jobCounts,
  schedulePublishing,
  values,
}) {
  const [expandedFilters, setExpandedFilters] = useState(false);
  const { dateRange, linesToPublish, lineTypesToPublish } = filters;

  const jobLimitExceeded = jobCounts.total > MAX_JOB_COUNT;
  const jobsInRange = jobCounts.total > 0 && !jobLimitExceeded;
  const hasDateRange = dateRange.startDate && dateRange.endDate;
  const submitEnabled = jobsInRange && hasDateRange && !schedulePublishing;

  const linesOptions = lines.map((line) => ({ label: line.name, value: line.id }));
  const lineTypesOptions = sortBy(
    uniqBy(
      lines.map((line) => ({ label: line.lineType, value: line.lineType })),
      "value",
    ),
    "label",
  );

  const searchingByLine = linesToPublish.length > 0 || lineTypesToPublish.length > 0;

  const handleDateRangeChange = ({ value, fieldName }) => {
    onFilterChange({ dateRange: { ...values.dateRange, [fieldName]: value } });
  };

  const handleLinesToPublishChange = (updatedLines) => {
    onFilterChange({ linesToPublish: updatedLines });
  };

  const handleLineTypesToPublishChange = (updatedLineTypes) => {
    onFilterChange({ lineTypesToPublish: updatedLineTypes });
  };

  const toggleExpandedFilters = () => {
    setExpandedFilters(!expandedFilters);
  };

  const dateRangeError = values.dateRange?.startDate > values.dateRange?.endDate;
  const datePickerClassNames = classNames("customDatePicker", { error: dateRangeError });

  return (
    <>
      <ApolloLoadingAnimation fetching={schedulePublishing} />
      <Text>
        Publishing a block will create a corresponding job under its work order. If a block was previously published,
        then updates will be reflected in its existing job.
      </Text>
      <Flex alignItems="flex-end" className={datePickerClassNames} pb="x1" pt="x1">
        <Box width={45 / 100}>
          <DatePicker
            dateFormat="MMM d, yyyy h:mm a"
            field="dateRange[startDate]"
            onChange={({ value }) => handleDateRangeChange({ fieldName: "startDate", value })}
            placeholderText="Start date, time"
          />
        </Box>
        <Box width={10 / 100}>
          <DateRangeSeparator>&mdash;</DateRangeSeparator>
        </Box>
        <Box width={45 / 100}>
          <DatePicker
            dateFormat="MMM d, yyyy h:mm a"
            field="dateRange[endDate]"
            onChange={({ value }) => handleDateRangeChange({ fieldName: "endDate", value })}
            placeholderText="End date, time"
          />
        </Box>
      </Flex>
      {dateRangeError && <InlineValidation errorMessage="The end date must be after the start date." mt="x1" />}
      {expandedFilters && (
        <>
          <Box data-testid="line-select">
            <FilterLabel
              marginBottom={0}
              marginTop="x1"
              value={
                <>
                  Line(s) <OptionalNotice>(Optional)</OptionalNotice>
                </>
              }
            />
            <Select
              closeMenuOnSelect={false}
              marginTop="x1"
              maxHeight={5 * DROPDOWN_ROW_HEIGHT}
              multiselect
              onChange={handleLinesToPublishChange}
              options={linesOptions}
              placeholder="Select lines..."
              value={linesToPublish}
            />
          </Box>
          <Box data-testid="line-types-select" paddingBottom="x1">
            <FilterLabel
              marginBottom={0}
              marginTop="x2"
              value={
                <>
                  Line type(s) <OptionalNotice>(Optional)</OptionalNotice>
                </>
              }
            />
            <Select
              closeMenuOnSelect={false}
              marginTop="x1"
              maxHeight={3 * DROPDOWN_ROW_HEIGHT}
              multiselect
              onChange={handleLineTypesToPublishChange}
              options={lineTypesOptions}
              placeholder="Select line types..."
              value={lineTypesToPublish}
            />
          </Box>
        </>
      )}
      <ExpandFiltersButton expandedFilters={expandedFilters} onClick={toggleExpandedFilters} />
      {jobCounts.onInactiveLines > 0 && (
        <Alert style={{ marginBottom: "16px" }} type="warning">
          This date range contains blocks scheduled on inactive lines. Corresponding jobs will be created for them,
          unless they are unscheduled.
        </Alert>
      )}
      <ModalFooter alignItems="center" justifyContent="flex-start">
        <Box marginRight="x2">
          <PrimaryButton className="pendo-create-jobs" disabled={!submitEnabled} form={FORM_ID} type="submit">
            Publish
          </PrimaryButton>
        </Box>
        {!isSubmitting && showJobCount && (
          <Text className="spec-publish-modal-job-count-message" data-testid="job-count-message">
            {nothingToPublishMessage({ jobCounts, searchingByLine })}
            {jobsCreatedAndUpdatedMessage({ jobCounts })}
            {jobLimitExceeded && (
              <Text color="red" data-testid="publish-job-limit-warning" inline>
                Only {MAX_JOB_COUNT} jobs can be published at a time. Please reduce the selected date range and try
                again.
              </Text>
            )}
          </Text>
        )}
      </ModalFooter>
    </>
  );
}
